/**
 * Per-trial quality measures shown in the Recordings table.
 *
 * Calibration quality comes from `calib_worst_cam_loo_px`: the worst camera's
 * leave-one-out reprojection error, in pixels. Lower is better.
 *
 * Tracking quality comes from `pose_cam_support`: camera-support score from 0
 * (worst) to 1 (best). Higher is better.
 *
 * Both cutoffs depend on the camera rig, so they live here as constants rather
 * than inline in the table.
 */

/*
 * ---------------------------------------------------------------------------
 * TUNING KNOBS — the only thing you need to edit to re-grade either measure.
 *
 * Calibration (px, lower is better): two levels — below `good` px is Good;
 * at or above `good` is Poor (no Fair band).
 *
 * Tracking (0–1, higher is better): above `fairMax` is Good; `fairMin`–`fairMax`
 * is Fair; below `fairMin` is Poor. 1 is best.
 * ---------------------------------------------------------------------------
 */
export const CALIBRATION_QUALITY_THRESHOLDS = { good: 15 };
export const TRACKING_QUALITY_THRESHOLDS = { fairMin: 0.5, fairMax: 0.8 };

export const QUALITY_UNAVAILABLE_DISPLAY = '—';

const TIER_LABELS = {
  good: 'Good',
  fair: 'Fair',
  poor: 'Poor',
  unknown: 'Not available',
};

export function getQualityTierLabel(tier) {
  return TIER_LABELS[tier] || TIER_LABELS.unknown;
}

/** Accepts the numeric strings a JSON API may send; anything else reads as missing. */
export function toQualityNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function unavailable(title) {
  return {
    value: null,
    tier: 'unknown',
    label: TIER_LABELS.unknown,
    display: QUALITY_UNAVAILABLE_DISPLAY,
    title,
  };
}

export function getCalibrationQuality(trial) {
  const value = toQualityNumber(trial && trial.calib_worst_cam_loo_px);
  if (value === null || value < 0) {
    return unavailable('Calibration quality is available once the trial has been processed.');
  }
  const tier = value < CALIBRATION_QUALITY_THRESHOLDS.good ? 'good' : 'poor';
  const display = `${value.toFixed(2)} px`;
  return {
    value,
    tier,
    label: TIER_LABELS[tier],
    display,
    title: `Worst camera leave-one-out reprojection error: ${display}. Lower is better; below ${CALIBRATION_QUALITY_THRESHOLDS.good} px is good, ${CALIBRATION_QUALITY_THRESHOLDS.good} px or above is poor.`,
  };
}

export function getTrackingQuality(trial) {
  const value = toQualityNumber(trial && trial.pose_cam_support);
  // Non-positive scores mean the pipeline has not filled this column yet.
  if (value === null || value <= 0) {
    return unavailable('Tracking quality is available once the trial has been processed.');
  }
  let tier = 'poor';
  if (value > TRACKING_QUALITY_THRESHOLDS.fairMax) tier = 'good';
  else if (value >= TRACKING_QUALITY_THRESHOLDS.fairMin) tier = 'fair';
  const display = value.toFixed(2);
  return {
    value,
    tier,
    label: TIER_LABELS[tier],
    display,
    title: `Camera support score: ${display} (1 is best). Above ${TRACKING_QUALITY_THRESHOLDS.fairMax} is good; ${TRACKING_QUALITY_THRESHOLDS.fairMin}–${TRACKING_QUALITY_THRESHOLDS.fairMax} is fair; below ${TRACKING_QUALITY_THRESHOLDS.fairMin} is poor.`,
  };
}

/**
 * Sort comparator for the quality columns. Rows without a measure always sink
 * to the bottom so flipping the sort direction still surfaces real values.
 */
export function compareQualityValues(a, b, ascending) {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return ascending ? a - b : b - a;
}
